import { useRef, useState } from 'react';
import { Loader2, Sparkles, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { AttachmentPreviewList, useStagedFiles } from '@shared/intake';
import type { IntakeSource } from '../lib/intakeExtraction';

// Chromeless intake surface. Renders the paste-text/image + upload-file side-by-side
// layout the workspace shows in the line-items empty state (mirrors the quotation
// pattern). Input-collection ONLY — the caller runs the extraction via the store
// (86d3a2agk) so the re-extract modal can fire-and-close. Text + screenshots + files are
// submitted together and extracted as one deduped set (86d3hdy52).

interface Props {
  /** Fired when the user clicks Extract with a valid source. The caller owns the
   *  extraction (salesOrderStore.runExtraction); this panel does not fetch. */
  onExtract: (source: IntakeSource) => void;
  /** Called right before extraction runs. Return false to abort (e.g. the user
   *  declined a replace-confirm). Defaults to always-proceed. */
  onBeforeExtract?: () => boolean;
  /** Extraction in flight for the active draft — disables inputs + shows the
   *  button's busy state. Driven by the store. */
  isExtracting?: boolean;
  /** When true, render a tighter layout suited for an inline empty state
   *  (no top heading, smaller padding). Defaults to false. */
  compact?: boolean;
}

const ACCEPT_EXTS = '.pdf,.png,.jpg,.jpeg,.xlsx,.xlsm,.csv';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB — matches the backend per-file ceiling

export function SalesOrderIntakePanel({
  onExtract,
  onBeforeExtract,
  isExtracting = false,
  compact = false,
}: Props) {
  const [text, setText] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { staged, rawFiles, hasFiles, addFiles, addFromClipboard, removeAt, clear } =
    useStagedFiles({ maxSize: MAX_FILE_SIZE, onReject: toast.error });

  const canExtract = !isExtracting && (text.trim().length > 0 || hasFiles);

  function handleExtract() {
    if (!canExtract) return;
    if (onBeforeExtract && !onBeforeExtract()) return;
    onExtract({ text, files: rawFiles });
    // Clear the staging area — the source now lives with the store action. (For the modal
    // this panel unmounts on close anyway.)
    setText('');
    clear();
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <div className={cn('space-y-3', compact ? '' : 'p-1')}>
      <div className="grid gap-3 md:grid-cols-2">
        {/* Paste text or image */}
        <div className="space-y-1.5">
          <label
            htmlFor="so-paste-text"
            className="text-xs font-medium text-muted-foreground"
          >
            Paste Text or Image
          </label>
          <Textarea
            id="so-paste-text"
            placeholder="Paste client email, chat, or order list… or paste a screenshot (Ctrl/⌘+V)"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={(e) => {
              if (isExtracting) return;
              if (addFromClipboard(e)) {
                e.preventDefault();
                toast.success('Screenshot added');
              }
            }}
            rows={8}
            disabled={isExtracting}
            className="resize-none font-mono text-xs"
          />
        </div>

        {/* Upload file(s) — drop zone */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            Upload File(s) <span className="font-normal">— multiple supported</span>
          </label>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              if (!isExtracting) setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragOver(false);
              if (isExtracting) return;
              addFiles(e.dataTransfer.files);
            }}
            disabled={isExtracting}
            className={cn(
              'flex h-44 w-full flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed text-xs transition-colors',
              isDragOver
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-border bg-muted/20 text-muted-foreground hover:bg-muted/40',
            )}
          >
            <Upload className="h-5 w-5" />
            <span className="font-medium">Drop file(s) here or click to upload</span>
            <span className="text-[10px] opacity-80">
              PDF, PNG, JPG, XLSX, XLSM, CSV (max 10 MB each)
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple
            accept={ACCEPT_EXTS}
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <AttachmentPreviewList
            items={staged}
            onRemove={removeAt}
            disabled={isExtracting}
          />
        </div>
      </div>

      <Button
        type="button"
        onClick={handleExtract}
        disabled={!canExtract}
        className="w-full"
      >
        {isExtracting ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" />
        )}
        Extract items
      </Button>
    </div>
  );
}
